import axios, { HttpStatusCode } from "axios";
import { API_CONFIG, MAIN_URL, ModulePath } from "../../constant/ApplicationVariables";
import dayjs from "dayjs";
import { extendToken_API } from "../controller/authController";
import sessionManager from "../../api/useAxios/SessionManager";

let timeout = API_CONFIG.timeout;

const axiosVar = axios;
axiosVar.defaults.timeout = timeout;

export let axiosInstance = axiosVar.create({
  baseURL: MAIN_URL.url,
  timeout: timeout,
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Track requests that are currently in-progress for token refresh
let refreshPromise = null;

// Request interceptor
axiosInstance.interceptors.request.use(
  async (config) => {
    let token = localStorage.getItem("access-token");
    const profileDetails = JSON.parse(localStorage.getItem("profileDetails") || "{}");
    const validTill = profileDetails?.validTill;
    
    // Reset session timer on API request (user is active)
    if (sessionManager && sessionManager.resetTimer) {
      sessionManager.resetTimer();
    }
    
    console.log("Request interceptor - token exists:", !!token);
    config.timeout = timeout;

    if (config.contentType === "formData") {
      config = await contentTypeReq(config);
    }
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add a flag to prevent retry loops
    config._retryAttempted = config._retryAttempted || false;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor with proper retry logic
axiosInstance.interceptors.response.use(
  (response) => {
    // Reset any refresh flags on successful response
    isRefreshing = false;
    refreshPromise = null;
    
    // Reset session timer on successful response (user is active)
    if (sessionManager && sessionManager.resetTimer) {
      sessionManager.resetTimer();
    }
    
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Prevent crash if config missing
    if (!originalRequest) {
      return Promise.reject(error);
    }

    // CRITICAL FIX: Add a unique retry flag to prevent infinite loops
    if (originalRequest._isRetryRequest) {
      console.log('Retry request failed again, redirecting to login');
      localStorage.clear();
      if (sessionManager && sessionManager.logout) {
        sessionManager.logout();
      }
      if (window.location.pathname !== ModulePath.login) {
        window.location.href = ModulePath.login;
      }
      return Promise.reject(error);
    }

    // Check if this is the token extension endpoint
    const isTokenExtensionEndpoint = 
      originalRequest.url?.includes("extend-token") || 
      originalRequest.url?.includes("refresh-token");

    // Don't attempt to refresh if this is already a refresh request
    if (isTokenExtensionEndpoint) {
      console.log('Token extension endpoint failed, redirecting to login');
      localStorage.clear();
      if (sessionManager && sessionManager.logout) {
        sessionManager.logout();
      }
      if (window.location.pathname !== ModulePath.login) {
        window.location.href = ModulePath.login;
      }
      return Promise.reject(error);
    }

    const shouldRefresh = error.response?.status === HttpStatusCode.Unauthorized;
    const shouldLogout = error.response?.status === 407;

    // Handle proxy/auth failure
    if (shouldLogout) {
      console.log('Logout status received, clearing session');
      localStorage.clear();
      if (sessionManager && sessionManager.logout) {
        sessionManager.logout();
      }
      if (window.location.pathname !== ModulePath.login) {
        window.location.href = ModulePath.login;
      }
      return Promise.reject(error);
    }

    // Handle token refresh for unauthorized requests
    if (shouldRefresh && !isTokenExtensionEndpoint && !originalRequest._isRetryRequest) {
      
      // If a refresh is already in progress, queue this request
      if (isRefreshing && refreshPromise) {
        console.log('Refresh in progress, queueing request');
        try {
          const newToken = await refreshPromise;
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          originalRequest._isRetryRequest = true; // Mark as retry
          return axiosInstance(originalRequest);
        } catch (err) {
          return Promise.reject(err);
        }
      }

      // Start refresh process
      isRefreshing = true;
      
      console.log('Attempting to refresh token...');
      
      // Store the refresh promise so subsequent requests can await it
      refreshPromise = (async () => {
        try {
          const response = await extendToken_API();
          const newToken = response?.data?.authToken;

          if (!newToken) {
            throw new Error("Invalid token response from refresh endpoint");
          }

          // Update localStorage with new token
          localStorage.setItem("access-token", newToken);
          localStorage.setItem("profileDetails", JSON.stringify(response.data));

          // Reset session timer after successful refresh
          if (sessionManager && sessionManager.resetTimer) {
            sessionManager.resetTimer();
          }

          console.log('Token refreshed successfully');
          return newToken;

        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          throw refreshError;
        }
      })();

      try {
        const newToken = await refreshPromise;
        
        // Process any queued requests
        processQueue(null, newToken);
        
        // Update the original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        originalRequest._isRetryRequest = true; // Mark as retry to prevent loops
        
        // Reset flags
        isRefreshing = false;
        refreshPromise = null;
        
        // Retry the original request
        return axiosInstance(originalRequest);

      } catch (refreshError) {
        console.error('Token refresh failed - clearing session');
        
        // Process queue with error
        processQueue(refreshError, null);
        
        // Clear localStorage on refresh failure
        localStorage.clear();
        
        // Reset flags
        isRefreshing = false;
        refreshPromise = null;
        
        // Redirect to login if not already there
        if (window.location.pathname !== ModulePath.login) {
          if (sessionManager && sessionManager.logout) {
            sessionManager.logout();
          }
          window.location.href = ModulePath.login;
        }
        
        return Promise.reject(refreshError);
      }
    }

    // Return other errors as is
    return Promise.reject(error);
  }
);

// Helper function to handle form data conversion
const contentTypeReq = async (config) => {
  if (config.contentType === "formData") {
    let body = config.data;
    let keys = Object.keys(body);
    let formData = new FormData();
    keys.forEach((key) => {
      formData.append(key, body[key]);
    });
    config.data = formData;
  }
  return config;
};

// Optional: Create a separate axios instance for public endpoints (no auth)
export const publicAxiosInstance = axiosVar.create({
  baseURL: MAIN_URL.url,
  timeout: timeout,
});

// Optional: Helper function to check if token is expired
export const isTokenExpired = () => {
  const profileDetails = JSON.parse(localStorage.getItem("profileDetails") || "{}");
  const validTill = profileDetails?.validTill;
  
  if (!validTill) return true;
  
  const now = dayjs();
  const expiry = dayjs(validTill);
  const isExpired = now.isAfter(expiry);
  
  console.log('Token expired:', isExpired, 'Expiry:', validTill);
  return isExpired;
};

// Optional: Helper function to get remaining token time
export const getTokenRemainingTime = () => {
  const profileDetails = JSON.parse(localStorage.getItem("profileDetails") || "{}");
  const validTill = profileDetails?.validTill;
  
  if (!validTill) return 0;
  
  const now = dayjs();
  const expiry = dayjs(validTill);
  const remainingSeconds = expiry.diff(now, 'seconds');
  
  return Math.max(0, remainingSeconds);
};

export default axiosInstance;